import { useCart } from '@shopify/hydrogen-react';
import { CartLine } from '@shopify/hydrogen-react/storefront-api-types';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { loadElevarUserData } from './elevar';
import { getElevarCartAttributes, isElevarReady } from './elevarIdentity';
import { cartAttributesUpdate } from './storefront/cart/actions';
import { useStoreActions, useStoreState } from '../store';

const STAMP_MAX_ATTEMPTS = 20;
const STAMP_RETRY_MS = 500;

const ElevarInitialization = () => {
  const { id: cartId, lines, cost } = useCart();
  const { setElevarCustomerData, setIsElevarInitializationFinished, setArrivedFromPage } =
    useStoreActions(actions => actions.analytics);
  const { elevarCustomerData } = useStoreState(state => state.analytics);
  const stampedCartIdRef = useRef<string | null>(null);

  useEffect(() => {
    setElevarCustomerData(null);
  }, [setElevarCustomerData]);

  const location = useLocation();

  useEffect(() => {
    window.ElevarInvalidateContext?.();
    setIsElevarInitializationFinished(false);
    loadElevarUserData(
      elevarCustomerData,
      lines ? (lines as CartLine[]) : [],
      cost?.totalAmount?.amount
    );
    setIsElevarInitializationFinished(true);

    return () => {
      setArrivedFromPage(location.pathname);
    };
  }, [
    location,
    lines,
    cost,
    elevarCustomerData,
    setArrivedFromPage,
    setIsElevarInitializationFinished,
  ]);

  useEffect(() => {
    if (!cartId || stampedCartIdRef.current === cartId) return;

    let cancelled = false;

    const attempt = (remaining: number) => {
      if (cancelled) return;
      if (!isElevarReady()) {
        if (remaining > 0) setTimeout(() => attempt(remaining - 1), STAMP_RETRY_MS);
        return;
      }
      const attrs = getElevarCartAttributes();
      if (attrs.length === 0) return;
      stampedCartIdRef.current = cartId;
      cartAttributesUpdate(cartId, attrs).catch(() => {
        stampedCartIdRef.current = null;
      });
    };

    attempt(STAMP_MAX_ATTEMPTS);

    return () => {
      cancelled = true;
    };
  }, [cartId]);

  return null;
};

export default ElevarInitialization;
